import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { App } from 'antd';
import originAxios from 'axios';

import { errorText } from '../../shared/constants/errorText';
import { ArgsProps } from 'antd/es/notification';

const withErrorHandler = (WrappedComponent: any, axios: any) => {
  const Wrapper = (props: any) => {
    const { notification } = App.useApp();

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [error, setError] = useState(false);
    const [didMount, setDidMount] = useState(false);

    useEffect(() => {
      if (axios.interceptors.request.handlers.length || axios.interceptors.response.handlers.length) {
        setDidMount(true);
        return;
      }

      const requestInterceptor = axios.interceptors.request.use((req: any) => {
        if (req && req.url) {
          const isRequestToAmazonS3 = req.url.includes('.s3.') && req.url.includes('.amazonaws.com');

          let authToken = (window as any).REACT_AUTH_TOKEN;

          if (!authToken) {
            authToken = localStorage.getItem('x-nc-code');
          }

          if (!isRequestToAmazonS3 && authToken) {
            req.headers['x-nc-code'] = authToken;
          }

          setErrorMessage(null);
          return req;
        }
        return req;
      });

      const responseInterceptor = axios.interceptors.response.use(
        (res: any) => res,
        async (error: any) => {
          if (
            originAxios.isCancel(error) &&
            error.message &&
            [errorText.canceledByUnmountComponent, errorText.canceledByUser, errorText.canceledByFetchingNewData].includes(error.message as any)
          ) {
            throw error;
          }

          if (!error || !error.response) {
            setErrorMessage(error.message || 'Something went wrong');
            setError(true);
            throw error;
          }

          switch (+error.response.status) {
            case 400:
              let responseData = error.response.data;
              const contentType = error.response.headers['content-type'];

              if (error.response.data instanceof Blob && contentType?.includes('application/json')) {
                const blob = new Blob([error.response.data]);
                const data = await blob.text();
                responseData = JSON.parse(data);
              }

              setErrorMessage(responseData.message || 'Bad Request');
              setError(true);
              break;
            case 401:
              setErrorMessage('Not Authorized. Please log out and log back in');
              setError(true);
              break;
            case 404:
              setErrorMessage('Not Found');
              setError(true);
              break;
            case 500:
              setErrorMessage(error.response.data.message || 'Something went wrong');
              setError(true);
              break;
            case 504:
              setErrorMessage('Internal Server Error');
              setError(true);
              break;
            default:
              setErrorMessage('Something went wrong');
              setError(true);
              break;
          }

          throw error;
        },
      );

      setDidMount(true);

      return () => {
        axios.interceptors.request.eject(requestInterceptor);
        axios.interceptors.response.eject(responseInterceptor);

        axios.interceptors.response.handlers = [];
        axios.interceptors.request.handlers = [];
      };
    }, []);

    useEffect(() => {
      if (error) {
        openNotificationWithIcon();
      }
    }, [error]);

    const openNotificationWithIcon = () => {
      notification.error({
        message: 'Error',
        top: 55,
        duration: 10,
        description: errorMessage,
        onClose: handleClose,
      } as ArgsProps);
    };

    const handleClose = () => {
      setError(false);
      setErrorMessage(null);
    };

    return didMount ? <WrappedComponent {...props} /> : null;
  };

  return connect(null, null)(Wrapper);
};

export default withErrorHandler;
